import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Divider,
  Chip,
  Button,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import { cloneDeep } from "lodash";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";
import ProducerForm from "./ProducerForm";

const PAGE_SIZE = 20;

const DEFAULT_FILTER = { name: "", status: "" };

export default function ProducersPage() {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTER);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [formOpen, setFormOpen] = useState(false);
  // null = create, otherwise the id of the producer being edited
  const [editingId, setEditingId] = useState(null);

  const handleReset = () => setFilters(cloneDeep(DEFAULT_FILTER));

  const handleCreate = () => {
    setEditingId(null);
    setFormOpen(true);
  };

  const handleEdit = (producer) => {
    setEditingId(producer.id);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingId(null);
    loadProducers(true);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const loadProducers = async (resetPage = false) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.name) params.append("name", filters.name);
      if (filters.status) params.append("status", filters.status);
      params.append("limit", String(PAGE_SIZE));
      params.append(
        "offset",
        String(resetPage ? 0 : paginationModel.page * PAGE_SIZE)
      );

      const response = await fetchWithInterceptor(
        `/api/producers?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch producers");
      const data = await response.json();
      setRows(data.rows || []);
      setRowCount(data.total ?? 0);
      if (resetPage) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
    } catch (err) {
      console.error(err);
      appContext.showMessage("Error loading producers", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducers(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

  useEffect(() => {
    loadProducers(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const columns = [
    {
      field: "name",
      headerName: "NAME",
      flex: 1,
    },
    {
      field: "status",
      headerName: "STATUS",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.status === "ACTIVE" ? "Active" : "Draft"}
          color={params.row.status === "ACTIVE" ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      width: 150,
      renderCell: (params) => (
        <Button
          size="small"
          variant="text"
          sx={{ minWidth: "auto", p: 0.5 }}
          onClick={() => handleEdit(params.row)}
        >
          EDIT
        </Button>
      ),
    },
  ];

  if (formOpen) {
    return <ProducerForm producerId={editingId} onClose={handleCloseForm} />;
  }

  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        Create, edit, and deactivate producers. Changes made here are synced to the "Producer" metaobject in Shopify and shown on the online store.
      </Typography>

      <Container sx={{ pb: 3 }} component="main" maxWidth="100%">
        <Paper
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            mt: 2,
            mb: 2,
          }}
        >
          <div style={{ padding: 16 }}>
            <Typography variant="h6">Search Filters</Typography>
            <br />
            <Grid container spacing={2} alignItems="center" sx={{ px: 0 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  label="Name"
                  name="name"
                  variant="outlined"
                  fullWidth
                  autoComplete="off"
                  value={filters.name}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  select
                  label="Status"
                  name="status"
                  variant="outlined"
                  fullWidth
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">ALL</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="DRAFT">Draft</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{ xs: 12, md: 2 }}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Button onClick={handleReset}>RESET FILTER</Button>
              </Grid>
            </Grid>
          </div>

          <Divider />

          <Grid container sx={{ px: 4, pb: 2, pt: 2, justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreate}
            >
              CREATE PRODUCER
            </Button>
          </Grid>

          <DataGrid
            rows={rows}
            columns={columns}
            paginationMode="server"
            disableColumnSorting
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            pageSizeOptions={[PAGE_SIZE]}
            disableSelectionOnClick
            disableColumnFilter
            loading={loading}
            getRowId={(row) => row.id}
            sx={{
              height: 52 * PAGE_SIZE + 120,
              border: "none",
              "& .MuiDataGrid-menuIcon": { display: "none" },
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "rgba(58, 53, 65, 0.87)",
              },
              "& .MuiDataGrid-columnsContainer": {
                backgroundColor: "rgb(249, 250, 252)",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "none",
              },
            }}
          />
        </Paper>
      </Container>
    </>
  );
}
